import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { FileSpreadsheet, Printer } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';
import { ResponsiveContainer, AreaChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart as RechartsPie, Pie, Cell } from 'recharts';

const COLORS = ['hsl(188, 64%, 38%)', 'hsl(164, 62%, 40%)', 'hsl(38, 95%, 55%)', 'hsl(4, 79%, 55%)', 'hsl(205, 92%, 45%)'];

export const ProfitLossTab = ({ loading, plData, exportToCsv, handlePrint }) => {
  if (loading) return <div className="text-center py-12">Loading P&L report...</div>;
  if (!plData) return null;

  return (
    <div className="space-y-6 print:space-y-4" id="pl-report">
      {/* P&L Header */}
      <div className="flex items-center justify-between print:hidden">
        <div>
          <h2 className="text-lg font-semibold">Profit & Loss Statement</h2>
          <p className="text-sm text-muted-foreground">
            Period: {plData.period.start} to {plData.period.end}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => exportToCsv(plData.expenses.by_category, 'profit_loss')}>
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-1" /> Print
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card data-testid="pl-revenue-card">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Net Revenue</p>
            <p className="text-2xl font-semibold font-mono text-[hsl(188,64%,38%)]">
              {formatCurrency(plData.revenue.net_revenue)}
            </p>
            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
              <span>{plData.revenue.invoice_count} invoices</span>
              <span>|</span>
              <span>Avg: {formatCurrency(plData.revenue.avg_invoice_value)}</span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="pl-expenses-card">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Total Expenses</p>
            <p className="text-2xl font-semibold font-mono text-[hsl(4,79%,55%)]">
              {formatCurrency(plData.expenses.total_expenses)}
            </p>
            <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
              <span>{plData.expenses.expense_count} expenses</span>
              <span>|</span>
              <span>{plData.kpis.expense_ratio.toFixed(1)}% of revenue</span>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="pl-gross-profit-card">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Gross Profit</p>
            <p className={`text-2xl font-semibold font-mono ${plData.profitability.gross_profit >= 0 ? 'text-[hsl(164,62%,40%)]' : 'text-[hsl(4,79%,55%)]'}`}>
              {formatCurrency(plData.profitability.gross_profit)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {plData.profitability.gross_margin.toFixed(1)}% margin
            </p>
          </CardContent>
        </Card>

        <Card className={plData.profitability.operating_profit >= 0 ? 'border-emerald-200 bg-emerald-50/30' : 'border-red-200 bg-red-50/30'} data-testid="pl-net-profit-card">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground">Net Profit</p>
            <p className={`text-2xl font-semibold font-mono ${plData.profitability.operating_profit >= 0 ? 'text-[hsl(164,62%,40%)]' : 'text-[hsl(4,79%,55%)]'}`}>
              {formatCurrency(plData.profitability.operating_profit)}
            </p>
            <Badge 
              variant="outline"
              className={plData.profitability.status === 'Profit' ? 'text-emerald-700 border-emerald-300 bg-emerald-100' : 'text-red-700 border-red-300 bg-red-100'}
            >
              {plData.profitability.status}
            </Badge>
          </CardContent>
        </Card>
      </div>

      {/* P&L Statement Table */}
      <Card>
        <CardHeader>
          <CardTitle>Income Statement</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableBody>
              <TableRow className="bg-slate-50 font-semibold">
                <TableCell colSpan={2}>REVENUE</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="pl-6">Gross Revenue</TableCell>
                <TableCell className="text-right font-mono">{formatCurrency(plData.revenue.gross_revenue)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="pl-8 text-muted-foreground">Service Fees</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{formatCurrency(plData.revenue.service_fees)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="pl-8 text-muted-foreground">Government Fees (Pass-through)</TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">{formatCurrency(plData.revenue.govt_fees)}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="pl-6 text-red-500">Less: Discounts</TableCell>
                <TableCell className="text-right font-mono text-red-500">({formatCurrency(plData.revenue.discounts)})</TableCell>
              </TableRow>
              <TableRow className="border-t-2 font-semibold">
                <TableCell className="pl-6">Net Revenue</TableCell>
                <TableCell className="text-right font-mono text-[hsl(188,64%,38%)]">{formatCurrency(plData.revenue.net_revenue)}</TableCell>
              </TableRow>

              <TableRow className="bg-slate-50 font-semibold">
                <TableCell colSpan={2}>COST OF SALES</TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="pl-6">Government Fees Paid</TableCell>
                <TableCell className="text-right font-mono text-red-500">({formatCurrency(plData.revenue.govt_fees)})</TableCell>
              </TableRow>
              <TableRow className="border-t-2 font-semibold">
                <TableCell className="pl-6">Gross Profit</TableCell>
                <TableCell className={`text-right font-mono ${plData.profitability.gross_profit >= 0 ? 'text-[hsl(164,62%,40%)]' : 'text-[hsl(4,79%,55%)]'}`}>
                  {formatCurrency(plData.profitability.gross_profit)}
                </TableCell>
              </TableRow>

              <TableRow className="bg-slate-50 font-semibold">
                <TableCell colSpan={2}>OPERATING EXPENSES</TableCell>
              </TableRow>
              {plData.expenses.by_category.map((cat) => (
                <TableRow key={cat.category}>
                  <TableCell className="pl-6">{cat.category}</TableCell>
                  <TableCell className="text-right font-mono text-red-500">({formatCurrency(cat.amount)})</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t font-medium">
                <TableCell className="pl-6">Total Operating Expenses</TableCell>
                <TableCell className="text-right font-mono text-red-500">({formatCurrency(plData.expenses.total_expenses)})</TableCell>
              </TableRow>

              <TableRow className="border-t-4 border-double bg-slate-100">
                <TableCell className="font-bold text-lg">NET PROFIT / (LOSS)</TableCell>
                <TableCell className={`text-right font-mono font-bold text-lg ${plData.profitability.operating_profit >= 0 ? 'text-[hsl(164,62%,40%)]' : 'text-[hsl(4,79%,55%)]'}`}>
                  {formatCurrency(plData.profitability.operating_profit)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 print:hidden">
        <Card>
          <CardHeader>
            <CardTitle>Monthly Profit Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={plData.monthly_trend}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Area type="monotone" dataKey="revenue" name="Revenue" fill="hsl(188, 64%, 85%)" stroke="hsl(188, 64%, 38%)" />
                <Area type="monotone" dataKey="expenses" name="Expenses" fill="hsl(4, 79%, 85%)" stroke="hsl(4, 79%, 55%)" />
                <Line type="monotone" dataKey="profit" name="Profit" stroke="hsl(164, 62%, 40%)" strokeWidth={2} dot />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Expense Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <RechartsPie>
                <Pie
                  data={plData.expenses.by_category}
                  dataKey="amount"
                  nameKey="category"
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  label={({ category, percent }) => `${category.substring(0, 10)}... ${(percent * 100).toFixed(0)}%`}
                >
                  {plData.expenses.by_category.map((entry, index) => (
                    <Cell key={`cell-${entry.category}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </RechartsPie>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
